import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useMassagerForgotPassword } from '@/hooks/massager/useMassagerAuth';
import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import {
  massagerForgotPasswordFormSchema,
  type MassagerForgotPasswordFormData,
} from '@/schemas/massagerForgotPasswordSchema';
import { AppAdminFormLabel, AppButton, AppForm, AppTextField } from '@/components/atoms';
import useAppToast from '@/hooks/useAppToast';

const MAX_INPUT_LENGTH = 255;

/** Enforce maxLength on Ant Design Input (which only shows a counter by default). */
const enforceMaxLength = (
  e: React.ChangeEvent<HTMLInputElement>,
  fieldOnChange: (...args: unknown[]) => void,
) => {
  const value = e.target.value.length > MAX_INPUT_LENGTH
    ? e.target.value.slice(0, MAX_INPUT_LENGTH)
    : e.target.value;
  fieldOnChange(value);
};

const TOAST_ERROR_MESSAGE = 'エラーが発生しました。';
const INVALID_CLINIC_OR_USER_MESSAGE = '治療院IDもしくは施術者ログインIDが不正です';
const WRONG_SITE_TYPE_MESSAGE = 'この治療院IDは当サイトではご利用いただけません。';
const UNAVAILABLE_CLINIC_MESSAGE = 'この治療院IDは現在利用できません。';

const isInvalidClinicOrUserMessage = (message?: string | null): boolean => {
  if (!message) return false;
  return (
    message.includes(INVALID_CLINIC_OR_USER_MESSAGE) ||
    message.includes('治療院IDが見つかりません') ||
    message.includes('ユーザIDが見つかりません')
  );
};

const isWrongSiteTypeMessage = (message?: string | null): boolean => {
  if (!message) return false;
  return message.includes('当サイトではご利用いただけません');
};

const isUnavailableClinicMessage = (message?: string | null): boolean => {
  if (!message) return false;
  return message.includes('現在利用できません');
};

const isNoReplyEmailMessage = (message?: string | null): boolean => {
  if (!message) return false;
  return (
    message.includes('このアカウントにはメールアドレスが設定されていません') ||
    message.includes('メールアドレスが設定されていません')
  );
};

const MassagerForgotPasswordPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const toast = useAppToast();

  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
    isUnreachable: isSiteUnreachable,
    refetch: refetchSiteCheck,
  } = useTenantSiteCheck(site);

  const validSite =
    !!site &&
    !isCheckingSite &&
    !isSiteCheckError &&
    !!siteCheck?.success &&
    !!siteCheck.data?.exists;

  const [isMailSent, setIsMailSent] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);

  const forgotPasswordMutation = useMassagerForgotPassword();
  const isLoading = forgotPasswordMutation.isPending;

  const isMedixFamily = site === 'medix' || site === 'upsystem';
  const NO_REPLY_EMAIL_MESSAGE = isMedixFamily
    ? '返信用メールアドレスが設定されていません。お手数ですが【03-3255-1095 審査部】までお問い合わせください'
    : '返信用メールアドレスが設定されていません。お手数ですが【システム管理者】までお問い合わせください';

  const {
    control,
    handleSubmit,
    setError: setFormError,
    clearErrors,
  } = useTrimmedForm<MassagerForgotPasswordFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(massagerForgotPasswordFormSchema),
    defaultValues: {
      clinic_number: '',
      login_id: '',
    },
  });

  const handleErrorMessage = (message: string) => {
    if (isWrongSiteTypeMessage(message)) {
      // Site mismatch — under 治療院ID field per spec.
      setFormError('clinic_number', { type: 'server', message: WRONG_SITE_TYPE_MESSAGE });
    } else if (isUnavailableClinicMessage(message)) {
      // Clinic / clinic info / therapist out of availability window — under 施術者ログインID field per spec.
      setFormError('login_id', { type: 'server', message: UNAVAILABLE_CLINIC_MESSAGE });
    } else if (isNoReplyEmailMessage(message)) {
      // Email not configured — under 施術者ログインID field per spec.
      setFormError('login_id', { type: 'server', message: NO_REPLY_EMAIL_MESSAGE });
    } else if (isInvalidClinicOrUserMessage(message)) {
      // Invalid clinic_number / login_id — under 施術者ログインID field per spec.
      setFormError('login_id', { type: 'server', message: INVALID_CLINIC_OR_USER_MESSAGE });
    } else {
      // Unrecognized server error — fall back to a form-level alert so the user
      // still sees what went wrong (toast alone is too generic for unknown causes).
      toast.error(TOAST_ERROR_MESSAGE);
      setFormErrorMessage(message);
    }
  };

  const onSubmit = async (data: MassagerForgotPasswordFormData) => {
    if (isSiteUnreachable) {
      // No answer from the site check: ask again; the global toast reports the connection.
      void refetchSiteCheck();
      return;
    }
    if (!validSite || !site) return;
    clearErrors();
    setIsMailSent(false);
    setFormErrorMessage(null);

    try {
      const response = await forgotPasswordMutation.mutateAsync({
        site: site || 'medix',
        clinicNumber: data.clinic_number,
        loginId: data.login_id,
      });

      if (response.success) {
        setSentMessage(response.message || 'パスワードリセット用のメールを送信いたしました。');
        setIsMailSent(true);
      } else {
        handleErrorMessage(response.message || 'エラーが発生しました。');
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        handleErrorMessage(axiosError.response?.data?.message || 'エラーが発生しました。');
      } else {
        setFormErrorMessage('エラーが発生しました。');
      }
    }
  };

  // Invalid site_type guard — same UX as LoginPage so a wrong /:site URL
  // never reaches the actual reset form.
  // A check with no answer keeps the form (Common network rule); only an answered 「no such site」 lands here.
  if (site && !isCheckingSite && !isSiteUnreachable && (!validSite || isSiteCheckError)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center overflow-x-hidden bg-[#fa7c64] px-4 py-8">
        <div className="w-full max-w-[420px] rounded-lg bg-white px-6 py-6 text-center shadow-lg">
          <h1 className="mb-3 text-lg font-bold text-red-600">無効なサイト</h1>
          <p className="mb-4 text-sm text-gray-600">指定されたサイトは存在しません。</p>
          <AppButton
            type="primary"
            onClick={() => navigate('/medix/massager/login')}
            style={{ backgroundColor: colors.primary }}
            className="w-full sm:w-auto"
            data-testid="massager-forgot-invalid-site-back"
          >
            ログイン画面へ
          </AppButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#fa7c64]">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <div className="rounded-lg bg-white px-5 py-6 shadow-lg sm:px-8">
            <h1 className="mb-4 text-center text-base font-bold text-gray-700 sm:text-lg">
                パスワードリセット
            </h1>

            {isMailSent && (
              <div
                role="status"
                className="mb-4 rounded border border-green-600 bg-green-50 px-3 py-2.5 text-left text-sm leading-relaxed text-green-900"
                data-testid="massager-forgot-success-message"
              >
                {sentMessage}
              </div>
            )}

            {!isMailSent && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                {formErrorMessage && (
                  <div
                    role="alert"
                    className="rounded border border-red-600 bg-red-50 px-3 py-2.5 text-left text-sm leading-relaxed text-red-900"
                    data-testid="massager-forgot-error-message"
                  >
                    {formErrorMessage}
                  </div>
                )}

                <Controller
                  name="clinic_number"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="治療院ID" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="clinic_number"
                        className="w-full"
                        autoComplete="username"
                        autoFocus
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-forgot-clinic-number-input"
                        size="large"
                      />
                    </AppForm.Item>
                  )}
                />

                <Controller
                  name="login_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppForm.Item
                      validateStatus={fieldState.error ? 'error' : undefined}
                      help={fieldState.error?.message}
                      className="mb-0"
                    >
                      <AppAdminFormLabel label="施術者ログインID" className="mb-1" />
                      <AppTextField
                        {...field}
                        onChange={(e) => enforceMaxLength(e, field.onChange)}
                        id="login_id"
                        className="w-full"
                        autoComplete="username"
                        maxLength={MAX_INPUT_LENGTH}
                        data-testid="massager-forgot-login-id-input"
                        size="large"
                      />
                    </AppForm.Item>
                  )}
                />

                <div className="pt-2">
                  <AppButton
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={isLoading}
                    disabled={isLoading}
                    className="w-full transition-opacity duration-200 hover:opacity-90"
                    style={{ backgroundColor: colors.primary }}
                    data-testid="massager-forgot-submit"
                  >
                    {isLoading ? '送信中...' : 'パスワードリセット用メールの送信'}
                  </AppButton>
                </div>
              </form>
            )}

            <div className="mt-4 text-center">
              <Link
                to={`/${site || 'medix'}/massager/login`}
                className="text-sm hover:opacity-80"
                style={{ color: colors.primary }}
                data-testid="massager-forgot-back-to-login"
              >
                ログイン画面に戻る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MassagerForgotPasswordPage;
